import React, { useCallback, useEffect, useState, createElement } from "react";
import { Pressable, StyleSheet, TextInput, View, useWindowDimensions } from "react-native";
import { CaretLeftIcon, CaretRightIcon, CheckIcon, PencilSimpleIcon, XIcon } from "@phosphor-icons/react";
import { DatePickerInput } from "react-native-paper-dates";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomHtml from "../../../../../components/common/general/CustomHtml/CustomHtml";
import { colors } from "../../../../../styles/design-system";
import type { SegmentFieldConfig, SegmentVariant, StructuredFieldEdits, StructuredFields } from "../types/reviewTypes";
import { hasHtmlMarkup } from "../utils/hasHtmlMarkup";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";

// ---------------------------------------------------------------------------
// Segment configurations per variant
// ---------------------------------------------------------------------------

const ACTIVITY_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
  { key: "dueDate", label: "DUE DATE", inputType: "date" },
  { key: "points", label: "POINTS POSSIBLE", inputType: "number" },
];

const PAGE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "PAGE CONTENT", inputType: "textarea" },
];

// Webpage variant shows title as a normal text field but renders the body as
// HTML source in a dedicated code-style textarea.
const WEBPAGE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "HTML BODY", inputType: "html" },
];

const LINK_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "url", label: "URL", inputType: "url" },
];

const CHECKLIST_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const RECOMMENDATION_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const MODULE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "NAME", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const VARIANT_SEGMENTS: Record<SegmentVariant, SegmentFieldConfig[]> = {
  activity: ACTIVITY_SEGMENTS,
  page: PAGE_SEGMENTS,
  webpage: WEBPAGE_SEGMENTS,
  link: LINK_SEGMENTS,
  checklist: CHECKLIST_SEGMENTS,
  recommendation: RECOMMENDATION_SEGMENTS,
  module: MODULE_SEGMENTS,
};

function parsePointsInput(value: string): number | null | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function getNextDirtyFieldKeys(
  previousKeys: Set<keyof StructuredFields>,
  fieldKey: keyof StructuredFields,
  nextValue: StructuredFields[keyof StructuredFields],
  proposedValue: StructuredFields[keyof StructuredFields]
): Set<keyof StructuredFields> {
  const nextKeys = new Set(previousKeys);
  if (Object.is(nextValue, proposedValue)) {
    nextKeys.delete(fieldKey);
    return nextKeys;
  }

  nextKeys.add(fieldKey);
  return nextKeys;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SegmentedDiffViewProps {
  variant: SegmentVariant;
  currentFields: StructuredFields;
  proposedFields: StructuredFields;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFieldEdits) => void;
  isExpanded?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SegmentedDiffView({
  variant,
  currentFields,
  proposedFields,
  isEditDisabled,
  onSaveEdit,
  isExpanded,
}: SegmentedDiffViewProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const allSegments = VARIANT_SEGMENTS[variant];
  const canEdit = Boolean(onSaveEdit) && !isEditDisabled;
  const {
    title: proposedTitle,
    description: proposedDescription,
    dueDate: proposedDueDate,
    points: proposedPoints,
    url: proposedUrl,
  } = proposedFields;

  // Filter out segments that have no data on either side (e.g., dueDate when not set)
  const segments = allSegments.filter((seg) => {
    const currentVal = currentFields[seg.key];
    const proposedVal = proposedFields[seg.key];
    return currentVal != null || proposedVal != null;
  });
  const hasDescriptionSegment = segments.some(
    (segment) => segment.key === "description" && segment.inputType !== "html"
  );

  const [currentCollapsed, setCurrentCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFields, setEditingFields] = useState<StructuredFields>({ ...proposedFields });
  const [dirtyFieldKeys, setDirtyFieldKeys] = useState<Set<keyof StructuredFields>>(new Set());
  const getRewriteProposedText = useCallback(() => editingFields.description ?? "", [editingFields.description]);
  const handleApplyRewrite = useCallback(
    (rewrittenText: string) => {
      setEditingFields((prev) => ({ ...prev, description: rewrittenText }));
      setDirtyFieldKeys((prev) =>
        getNextDirtyFieldKeys(prev, "description", rewrittenText, proposedFields.description)
      );
    },
    [proposedFields.description]
  );
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    currentText: currentFields.description,
    disabled: !canEdit,
    getProposedText: getRewriteProposedText,
    onApplyRewrite: handleApplyRewrite,
    title: editingFields.title,
  });

  // Reset editing state when proposed fields change (item switch)
  useEffect(() => {
    setIsEditing(false);
    setCurrentCollapsed(false);
    setEditingFields({
      title: proposedTitle,
      description: proposedDescription,
      dueDate: proposedDueDate,
      points: proposedPoints,
      url: proposedUrl,
    });
    setDirtyFieldKeys(new Set());
  }, [proposedTitle, proposedDescription, proposedDueDate, proposedPoints, proposedUrl]);

  // Auto-collapse current column when editing starts
  useEffect(() => {
    setCurrentCollapsed(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const handleEdit = useCallback(() => {
    if (!canEdit) return;
    setEditingFields({ ...proposedFields });
    setDirtyFieldKeys(new Set());
    setIsEditing(true);
  }, [canEdit, proposedFields]);

  const handleSaveEdit = useCallback(() => {
    if (!canEdit) return;
    const editedFieldPatch: StructuredFieldEdits = {};
    dirtyFieldKeys.forEach((fieldKey) => {
      editedFieldPatch[fieldKey] = editingFields[fieldKey];
    });
    setIsEditing(false);
    setDirtyFieldKeys(new Set());
    onSaveEdit?.(editedFieldPatch);
  }, [canEdit, dirtyFieldKeys, editingFields, onSaveEdit]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingFields({ ...proposedFields });
    setDirtyFieldKeys(new Set());
  }, [proposedFields]);

  const handleFieldChange = useCallback(
    (key: keyof StructuredFields, value: string) => {
      if (key !== "points") {
        setEditingFields((prev) => ({
          ...prev,
          [key]: value,
        }));
        setDirtyFieldKeys((prev) => getNextDirtyFieldKeys(prev, key, value, proposedFields[key]));
        return;
      }

      const parsedPoints = parsePointsInput(value);
      if (parsedPoints === undefined) {
        return;
      }

      setEditingFields((prev) => ({
        ...prev,
        points: parsedPoints,
      }));
      setDirtyFieldKeys((prev) => getNextDirtyFieldKeys(prev, "points", parsedPoints, proposedFields.points));
    },
    [proposedFields]
  );

  return (
    <View style={[styles.container, isExpanded && styles.containerExpanded]}>
      {/* Headers row */}
      <View
        style={[
          styles.headersRow,
          showTwoColumns && !currentCollapsed ? styles.headersRowDesktop : styles.headersRowMobile,
        ]}>
        {!currentCollapsed && (
          <View style={[styles.currentHeader, showTwoColumns && styles.headerHalf]}>
            <CustomText
              text="Current"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.currentHeaderText}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Collapse current"
              onPress={() => setCurrentCollapsed(true)}
              style={styles.iconButton}>
              <CaretLeftIcon size={14} color={colors.grey[500]} weight="bold" />
            </Pressable>
          </View>
        )}
        <View style={[styles.proposedHeader, !currentCollapsed && showTwoColumns && styles.headerHalf]}>
          <View style={styles.proposedHeaderLeft}>
            {currentCollapsed && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Expand current"
                onPress={() => setCurrentCollapsed(false)}
                style={styles.iconButtonSuccess}>
                <CaretRightIcon size={14} color={colors.success[700]} weight="bold" />
              </Pressable>
            )}
            <CustomText
              text="Proposed"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.proposedHeaderText}
            />
          </View>
          {isEditing ? (
            <View style={styles.editingActions}>
              <Pressable onPress={handleCancelEdit} style={styles.cancelButton}>
                <XIcon size={12} color={colors.grey[600]} weight="bold" />
                <CustomText text="Cancel" useTranslationText={false} size="xs" style={styles.cancelButtonText} />
              </Pressable>
              {hasDescriptionSegment && (
                <AIRewriteActionButton disabled={!canEdit || isRewriting} onPress={openRewriteModal} />
              )}
              <CustomButton
                text="Save"
                useTranslationText={false}
                size="xs"
                styleType="primary"
                leftIcon={<CheckIcon size={12} color={colors.white} weight="bold" />}
                onPress={handleSaveEdit}
                style={styles.saveButtonSize}
                textStyle={styles.saveButtonText}
              />
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Edit proposed changes"
              accessibilityState={{ disabled: !canEdit }}
              disabled={!canEdit}
              onPress={handleEdit}
              style={[styles.editButton, !canEdit && styles.editButtonDisabled]}>
              <PencilSimpleIcon size={14} color={canEdit ? colors.grey[700] : colors.grey[400]} weight="regular" />
              <CustomText
                text="Edit"
                useTranslationText={false}
                size="xs"
                style={[styles.editButtonText, !canEdit && styles.editButtonTextDisabled]}
              />
            </Pressable>
          )}
        </View>
      </View>

      {/* Segment rows */}
      {segments.map((seg, index) => {
        const proposedValue = (isEditing ? editingFields : proposedFields)[seg.key];
        const currentValue = currentFields[seg.key];
        const proposedDisplay = proposedValue != null ? String(proposedValue) : "";
        const currentDisplay = currentValue != null ? String(currentValue) : "";
        const hasChanged = currentDisplay !== proposedDisplay;
        const isLast = index === segments.length - 1;

        return (
          <View
            key={seg.key}
            style={[
              styles.segmentRow,
              showTwoColumns && !currentCollapsed ? styles.segmentRowDesktop : styles.segmentRowMobile,
              !isLast && styles.segmentRowBorder,
            ]}>
            {/* Current field */}
            {!currentCollapsed && (
              <View
                style={[
                  styles.fieldBlock,
                  showTwoColumns && styles.fieldBlockHalf,
                  showTwoColumns && styles.fieldBlockCurrentBorder,
                ]}>
                <View style={styles.fieldLabelRow}>
                  <CustomText
                    text={seg.label}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.fieldLabel}
                  />
                </View>
                {seg.inputType === "html" ? (
                  <HtmlField value={currentDisplay} />
                ) : seg.inputType === "textarea" && currentDisplay ? (
                  hasHtmlMarkup(currentDisplay) ? (
                    <CustomHtml html={currentDisplay} style={richContentBaseStyle} />
                  ) : (
                    <CustomText text={currentDisplay} useTranslationText={false} size="s" style={styles.fieldValue} />
                  )
                ) : (
                  <CustomText
                    text={currentDisplay || "Not set"}
                    useTranslationText={false}
                    size="s"
                    style={currentDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                  />
                )}
              </View>
            )}

            {/* Proposed field */}
            <View
              style={[
                styles.fieldBlock,
                !currentCollapsed && showTwoColumns && styles.fieldBlockHalf,
                hasChanged && !isEditing && styles.fieldBlockChanged,
                isExpanded && styles.fieldBlockExpanded,
              ]}>
              <View style={styles.fieldLabelRow}>
                <CustomText
                  text={seg.label}
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.proposedFieldLabel}
                />
                {!isEditing && hasChanged && (
                  <View style={styles.changedBadge}>
                    <CustomText
                      text="Changed"
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.changedBadgeText}
                    />
                  </View>
                )}
                {!isEditing && !hasChanged && currentDisplay !== "" && (
                  <CustomText text="No change" useTranslationText={false} size="xs" style={styles.noChangeBadgeText} />
                )}
              </View>
              {isEditing ? (
                <FieldInput config={seg} value={proposedDisplay} onChange={(v) => handleFieldChange(seg.key, v)} />
              ) : seg.inputType === "html" ? (
                <HtmlField value={proposedDisplay} />
              ) : seg.inputType === "textarea" && proposedDisplay ? (
                hasHtmlMarkup(proposedDisplay) ? (
                  <CustomHtml html={proposedDisplay} style={richContentBaseStyle} />
                ) : (
                  <CustomText text={proposedDisplay} useTranslationText={false} size="s" style={styles.fieldValue} />
                )
              ) : (
                <CustomText
                  text={proposedDisplay || "Not set"}
                  useTranslationText={false}
                  size="s"
                  style={proposedDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                />
              )}
            </View>
          </View>
        );
      })}
      <AIRewriteModal
        disabled={!canEdit}
        errorMessage={rewriteErrorMessage}
        isRewriting={isRewriting}
        onCancel={closeRewriteModal}
        onRewrite={submitRewrite}
        visible={isRewriteModalVisible}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// HTML Field with Source / Preview toggle
// ---------------------------------------------------------------------------

interface HtmlFieldProps {
  value: string;
  editable?: boolean;
  onChange?: (v: string) => void;
  placeholder?: string;
}

function HtmlField({ value, editable, onChange, placeholder }: HtmlFieldProps) {
  const [showPreview, setShowPreview] = useState(false);

  // Strip <script> tags before rendering in the iframe so no untrusted code
  // executes. Image src/href attributes and all other markup are preserved.
  const sanitizedForPreview = value
    ? value.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "").replace(/<script\b[^>]*\/>/gi, "")
    : "";
  const iframeContent = sanitizedForPreview || "<p style='color:#9ca3af;font-family:sans-serif'>No content</p>";

  const renderBody = () =>
    showPreview ? (
      <View>
        <View style={styles.htmlPreviewNote}>
          <CustomText
            text="⚠ Some images and media hosted in Brightspace may not display in this preview but will work correctly in Brightspace."
            useTranslationText={false}
            size="xs"
            style={styles.htmlPreviewNoteText}
          />
        </View>
        {createElement("iframe", {
          srcDoc: iframeContent,
          sandbox: "allow-popups",
          style: {
            width: "100%",
            minHeight: 320,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "white",
            display: "block",
          },
        })}
      </View>
    ) : editable ? (
      <TextInput
        value={value}
        onChangeText={onChange}
        multiline
        style={styles.htmlInput}
        textAlignVertical="top"
        placeholder={placeholder ?? "Paste or edit HTML here\u2026"}
        placeholderTextColor={colors.grey[500]}
      />
    ) : (
      <TextInput
        value={value || "Not set"}
        editable={false}
        multiline
        style={styles.htmlInputReadOnly}
        textAlignVertical="top"
      />
    );

  return (
    <View>
      <View style={styles.htmlToggleRow}>
        <Pressable
          onPress={() => setShowPreview(false)}
          style={[styles.htmlToggleBtn, !showPreview && styles.htmlToggleBtnActive]}>
          <CustomText
            text="Source"
            useTranslationText={false}
            size="xs"
            weight={!showPreview ? "bold" : "normal"}
            style={!showPreview ? styles.htmlToggleLabelActive : styles.htmlToggleLabel}
          />
        </Pressable>
        <Pressable
          onPress={() => setShowPreview(true)}
          style={[styles.htmlToggleBtn, showPreview && styles.htmlToggleBtnActive]}>
          <CustomText
            text="Preview"
            useTranslationText={false}
            size="xs"
            weight={showPreview ? "bold" : "normal"}
            style={showPreview ? styles.htmlToggleLabelActive : styles.htmlToggleLabel}
          />
        </Pressable>
      </View>
      {renderBody()}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Field Input
// ---------------------------------------------------------------------------

interface FieldInputProps {
  config: SegmentFieldConfig;
  value: string;
  onChange: (value: string) => void;
}

function FieldInput({ config, value, onChange }: FieldInputProps) {
  const isMultiline = config.inputType === "textarea";
  const isHtml = config.inputType === "html";
  const isNumber = config.inputType === "number";
  const isDate = config.inputType === "date";

  if (isDate) {
    // Parse the locale string ("3/21/2026") back to a Date for the picker,
    // and convert the picker's Date output back to a locale string.
    const dateValue = value ? new Date(value) : undefined;
    const validDateValue = dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue : undefined;

    return (
      <DatePickerInput
        locale="en"
        value={validDateValue}
        onChange={(d) => onChange(d ? d.toLocaleDateString("en-US") : "")}
        inputMode="start"
        mode="outlined"
        outlineColor={colors.grey[300]}
        activeOutlineColor={colors.brand[600]}
        selectionColor={false}
        withDateFormatInLabel={false}
        placeholder="MM/DD/YYYY"
        inputProps={{ color: colors.grey[900], mode: "outline" }}
        theme={{ roundness: 8 }}
        style={styles.dateInput}
      />
    );
  }

  if (isHtml) {
    return <HtmlField value={value} editable onChange={onChange} />;
  }

  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      multiline={isMultiline}
      keyboardType={isNumber ? "numeric" : config.inputType === "url" ? "url" : "default"}
      style={[styles.textInput, isMultiline && styles.textInputMultiline, isNumber && styles.textInputNumber]}
      textAlignVertical={isMultiline ? "top" : "center"}
      placeholder={`Enter ${config.label.toLowerCase()}`}
      placeholderTextColor={colors.grey[500]}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const richContentBaseStyle = {
  fontSize: 14,
  lineHeight: "22px",
  color: "#344054",
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  containerExpanded: {
    flex: 1,
    minHeight: 0,
    borderRadius: 0,
  },
  headersRow: {},
  headersRowDesktop: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headersRowMobile: {
    flexDirection: "column",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerHalf: {
    flex: 1,
  },
  currentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  currentHeaderText: {
    color: colors.grey[700],
  },
  proposedHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
  },
  proposedHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  proposedHeaderText: {
    color: colors.success[700],
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
  },
  iconButtonSuccess: {
    padding: 4,
    borderRadius: 4,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editButtonText: {
    color: colors.grey[700],
  },
  editButtonDisabled: {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    opacity: 0.7,
  },
  editButtonTextDisabled: {
    color: colors.grey[400],
  },
  editingActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: colors.grey[600],
  },
  saveButtonSize: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  saveButtonText: {
    color: colors.white,
  },
  // --- Segment rows ---
  segmentRow: {},
  segmentRowDesktop: {
    flexDirection: "row",
  },
  segmentRowMobile: {
    flexDirection: "column",
  },
  segmentRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  fieldBlock: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  fieldBlockExpanded: {
    flex: 1,
    minHeight: 0,
  },
  fieldBlockHalf: {
    flex: 1,
  },
  fieldBlockCurrentBorder: {
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  fieldBlockChanged: {
    backgroundColor: "rgba(240, 253, 244, 0.3)",
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  fieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  proposedFieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  changedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "#F3FEE7",
  },
  changedBadgeText: {
    color: "#3B7C0F",
    fontSize: 10,
  },
  noChangeBadgeText: {
    color: colors.grey[500],
    fontSize: 10,
  },
  fieldValue: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  fieldValueEmpty: {
    color: colors.grey[400],
    lineHeight: 22,
    fontStyle: "italic",
  },
  htmlInput: {
    minHeight: 320,
    padding: 12,
    fontSize: 12,
    lineHeight: 18,
    color: colors.grey[900],
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "monospace",
  },
  htmlInputReadOnly: {
    minHeight: 200,
    padding: 12,
    fontSize: 12,
    lineHeight: 18,
    color: colors.grey[700],
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 8,
    fontFamily: "monospace",
  },
  htmlToggleRow: {
    flexDirection: "row" as const,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 6,
    overflow: "hidden",
    alignSelf: "flex-start" as const,
  },
  htmlToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    backgroundColor: colors.white,
  },
  htmlToggleBtnActive: {
    backgroundColor: colors.grey[100],
  },
  htmlToggleLabel: {
    color: colors.grey[500],
  },
  htmlToggleLabelActive: {
    color: colors.grey[800],
  },
  htmlPreviewNote: {
    marginBottom: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "#FFFBEB",
    borderWidth: 1,
    borderColor: "#FDE68A",
    borderRadius: 6,
  },
  htmlPreviewNoteText: {
    color: "#92400E",
  },
  textInput: {
    padding: 10,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
  textInputMultiline: {
    minHeight: 200,
    textAlignVertical: "top",
  },
  textInputNumber: {
    width: 128,
  },
  dateInput: {
    backgroundColor: colors.white,
  },
});

export default SegmentedDiffView;
